import { useState } from "preact/hooks";
import type { FlexibleProgress } from "../types";

export interface FlexibleTaskRowProps {
  item: FlexibleProgress;
  onLog: (item: FlexibleProgress) => void;
}

/**
 * A variable-frequency chore row ("Aim for: N times this week (done M)") —
 * design-spec-today-card.md §4's flexible-cadence phrase. Unlike a normal
 * task row this never flips to a persistent "done" state (the target is
 * met over a rolling week, not a single tap), so the check circle shows a
 * brief "logged" pulse instead — satisfying tap feedback (S6) without
 * fabricating a done state the server doesn't report.
 *
 * Same whole-row tap target as `TaskRow` (one `<button>`, no nesting).
 */
export function FlexibleTaskRow({ item, onLog }: FlexibleTaskRowProps) {
  const [justLogged, setJustLogged] = useState(false);
  const metaId = `flex-meta-${item.cadence_id}`;

  function handleClick() {
    setJustLogged(true);
    setTimeout(() => setJustLogged(false), 260);
    onLog(item);
  }

  return (
    <li class="task-row" data-cadence-id={item.cadence_id}>
      <button
        type="button"
        class="task-row-main"
        aria-pressed="false"
        aria-label={`Mark '${item.title}' done`}
        aria-describedby={metaId}
        onClick={handleClick}
      >
        <span class={`check-circle${justLogged ? " is-done" : ""}`} aria-hidden="true">
          {justLogged ? <span class="check-glyph">✓</span> : null}
        </span>
        <span class="task-copy">
          <span class="task-title">{item.title}</span>
          <span class="task-meta" id={metaId}>
            {item.phrase}
          </span>
        </span>
      </button>
    </li>
  );
}
